import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import {
    ArrowLeft, Download, Upload, FileJson, FileSpreadsheet,
    Loader2, Check, X, AlertTriangle, Eye, Send,
    Megaphone, Trash2, Globe,
    Pencil, Save, Bell, Clock, Repeat, Code,
} from 'lucide-react';
import {
    getPushDomains, createPushCampaignBatch,
} from '@/services/api/push';
import { getMedia, uploadMedia, getMediaUrl, getThumbnailUrl } from '@/services/api';
import type { MediaItem } from '@/services/api/media';
import type { PushCampaignCreate } from '@/types/push';
import { CRON_PRESETS } from '@/types/push';
import { Image as ImageIcon, Upload as UploadIcon } from 'lucide-react';

type BatchCampaignItem = PushCampaignCreate & { _valid?: boolean; _errors?: string[] };

const TEMPLATE_FIELDS = [
    { key: 'name', label: 'Nome', required: true, example: 'Promo Black Friday' },
    { key: 'title', label: 'Titulo da Push', required: true, example: 'Nao perca! 50% OFF' },
    { key: 'body', label: 'Mensagem', required: true, example: 'Aproveite descontos incriveis por tempo limitado' },
    { key: 'url', label: 'URL destino', required: true, example: 'https://seusite.com/promo' },
    { key: 'type', label: 'Tipo (direct/scheduled/recurring)', required: false, example: 'direct' },
    { key: 'icon', label: 'URL do icone', required: false, example: '' },
    { key: 'image', label: 'URL da imagem', required: false, example: '' },
    { key: 'utm_campaign', label: 'UTM Campaign', required: false, example: 'black_friday' },
    { key: 'utm_medium', label: 'UTM Medium', required: false, example: 'push' },
    { key: 'utm_source', label: 'UTM Source', required: false, example: 'push_notification' },
    { key: 'tag_on_receive', label: 'Tag ao receber', required: false, example: '' },
    { key: 'tag_on_click', label: 'Tag ao clicar', required: false, example: '' },
    { key: 'scheduled_at', label: 'Agendar para (ISO)', required: false, example: '' },
    { key: 'recurring_cron', label: 'Cron recorrente', required: false, example: '' },
];

function validateCampaign(c: any): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    if (!c.name) errors.push('Nome e obrigatorio');
    if (!c.title) errors.push('Titulo e obrigatorio');
    if (!c.body) errors.push('Mensagem e obrigatoria');
    if (!c.url) errors.push('URL e obrigatoria');
    if (c.type && !['direct', 'scheduled', 'recurring'].includes(c.type)) errors.push('Tipo invalido');
    if (c.type === 'scheduled' && !c.scheduled_at) errors.push('Agendamento requer scheduled_at');
    if (c.type === 'recurring' && !c.recurring_cron) errors.push('Recorrente requer recurring_cron');
    return { valid: errors.length === 0, errors };
}

export default function CampaignBatch() {
    const [step, setStep] = useState<'upload' | 'preview'>('upload');
    const [selectedDomainId, setSelectedDomainId] = useState<number | null>(null);
    const [items, setItems] = useState<BatchCampaignItem[]>([]);
    const [editingIdx, setEditingIdx] = useState<number | null>(null);
    const [editDraft, setEditDraft] = useState<BatchCampaignItem | null>(null);
    const [previewItem, setPreviewItem] = useState<BatchCampaignItem | null>(null);
    const [showMediaPicker, setShowMediaPicker] = useState<'icon' | 'image' | 'badge' | null>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadMode, setUploadMode] = useState<'file' | 'editor'>('file');
    const [jsonCode, setJsonCode] = useState('[\n  {\n    "name": "",\n    "title": "",\n    "body": "",\n    "url": "",\n    "type": "scheduled",\n    "scheduled_at": ""\n  }\n]');
    const [jsonError, setJsonError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { toast } = useToast();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const { data: domains = [] } = useQuery({ queryKey: ['push-domains'], queryFn: getPushDomains });
    const configuredDomains = domains.filter((d) => d.push_config?.mongo_domain_id);
    const selectedDomain = configuredDomains.find(d => d.id === selectedDomainId)?.domain || '';

    const batchMutation = useMutation({
        mutationFn: createPushCampaignBatch,
        onSuccess: (data) => {
            toast({ title: `${data.length} campanhas criadas com sucesso!` });
            queryClient.invalidateQueries({ queryKey: ['push-campaigns'] });
            navigate('/push/campaigns');
        },
        onError: (e: any) => toast({ variant: 'destructive', title: 'Erro ao criar campanhas', description: e.message }),
    });

    const { data: mediaList = [], refetch: refetchMedia } = useQuery<MediaItem[]>({
        queryKey: ['media-list'],
        queryFn: () => getMedia(),
    });

    const handleMediaUpload = async (file: File) => {
        if (!showMediaPicker) return;
        setUploading(true);
        try {
            const isIcon = showMediaPicker === 'icon' || showMediaPicker === 'badge';
            const media = await uploadMedia(file, isIcon);
            updateDraft(showMediaPicker, getMediaUrl(media.filepath));
            refetchMedia();
            setShowMediaPicker(null);
            toast({ title: 'Upload concluido!' });
        } catch (e: any) {
            toast({ variant: 'destructive', title: 'Erro no upload', description: e.message });
        } finally {
            setUploading(false);
        }
    };

    const handleJsonSubmit = () => {
        if (!selectedDomainId) {
            toast({ variant: 'destructive', title: 'Selecione um dominio primeiro' });
            return;
        }
        try {
            let data = JSON.parse(jsonCode);
            if (!Array.isArray(data)) data = [data];
            setJsonError(null);

            const validated = data.map((item: any) => {
                const { valid, errors } = validateCampaign(item);
                return {
                    ...item,
                    type: item.type || 'direct',
                    domain_id: selectedDomainId,
                    _valid: valid,
                    _errors: errors,
                } as BatchCampaignItem;
            });

            setItems(validated);
            setStep('preview');
            toast({ title: `${validated.length} campanhas carregadas` });
        } catch (err: any) {
            setJsonError(err.message);
        }
    };

    const downloadJsonTemplate = () => {
        const template = [
            TEMPLATE_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: f.example }), {}),
            TEMPLATE_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: f.key === 'name' ? 'Campanha 2' : f.key === 'title' ? 'Novidades!' : f.key === 'body' ? 'Confira as ultimas novidades' : f.key === 'url' ? 'https://seusite.com/novidades' : f.example }), {}),
        ];
        const blob = new Blob([JSON.stringify(template, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = 'campanhas_template.json'; a.click();
        URL.revokeObjectURL(url);
    };

    const downloadCsvTemplate = () => {
        const headers = TEMPLATE_FIELDS.map(f => f.key).join(',');
        const row1 = TEMPLATE_FIELDS.map(f => `"${f.example}"`).join(',');
        const row2 = TEMPLATE_FIELDS.map(f => {
            if (f.key === 'name') return '"Campanha 2"';
            if (f.key === 'title') return '"Novidades!"';
            if (f.key === 'body') return '"Confira as ultimas novidades"';
            if (f.key === 'url') return '"https://seusite.com/novidades"';
            return `"${f.example}"`;
        }).join(',');
        const csv = `${headers}\n${row1}\n${row2}`;
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = 'campanhas_template.csv'; a.click();
        URL.revokeObjectURL(url);
    };

    const parseCsv = (text: string): any[] => {
        const lines = text.split('\n').filter(l => l.trim());
        if (lines.length < 2) return [];
        const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
        return lines.slice(1).map(line => {
            const values: string[] = [];
            let current = '';
            let inQuotes = false;
            for (const char of line) {
                if (char === '"') { inQuotes = !inQuotes; continue; }
                if (char === ',' && !inQuotes) { values.push(current.trim()); current = ''; continue; }
                current += char;
            }
            values.push(current.trim());
            const obj: any = {};
            headers.forEach((h, i) => { obj[h] = values[i] || ''; });
            return obj;
        });
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                let data: any[];
                if (file.name.endsWith('.json')) {
                    data = JSON.parse(ev.target?.result as string);
                    if (!Array.isArray(data)) data = [data];
                } else if (file.name.endsWith('.csv')) {
                    data = parseCsv(ev.target?.result as string);
                } else {
                    toast({ variant: 'destructive', title: 'Formato nao suportado', description: 'Use .json ou .csv' });
                    return;
                }

                const validated = data.map(item => {
                    const { valid, errors } = validateCampaign(item);
                    return {
                        ...item,
                        type: item.type || 'direct',
                        domain_id: selectedDomainId!,
                        _valid: valid,
                        _errors: errors,
                    } as BatchCampaignItem;
                });

                setItems(validated);
                setStep('preview');
                toast({ title: `${validated.length} campanhas carregadas` });
            } catch (err: any) {
                toast({ variant: 'destructive', title: 'Erro ao ler arquivo', description: err.message });
            }
        };
        reader.readAsText(file);
        e.target.value = '';
    };

    const removeItem = (idx: number) => {
        setItems(prev => prev.filter((_, i) => i !== idx));
        if (editingIdx === idx) { setEditingIdx(null); setEditDraft(null); }
    };

    const startEditing = (idx: number) => {
        setEditingIdx(idx);
        setEditDraft({ ...items[idx] });
    };

    const cancelEditing = () => {
        setEditingIdx(null);
        setEditDraft(null);
    };

    const saveEditing = () => {
        if (editingIdx === null || !editDraft) return;
        const { valid, errors } = validateCampaign(editDraft);
        const updated = { ...editDraft, _valid: valid, _errors: errors };
        setItems(prev => prev.map((item, i) => i === editingIdx ? updated : item));
        setEditingIdx(null);
        setEditDraft(null);
        toast({ title: valid ? 'Campanha atualizada!' : 'Campanha salva (ainda com erros)' });
    };

    const updateDraft = (field: string, value: any) => {
        setEditDraft(prev => prev ? { ...prev, [field]: value } : prev);
    };

    const validItems = items.filter(i => i._valid);
    const invalidItems = items.filter(i => !i._valid);

    const handleSubmit = () => {
        const campaigns: PushCampaignCreate[] = validItems.map(({ _valid, _errors, ...rest }) => rest);
        batchMutation.mutate(campaigns);
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="sm" onClick={() => navigate('/push/campaigns')} className="gap-1.5 rounded-xl">
                    <ArrowLeft className="h-4 w-4" /> Voltar
                </Button>
                <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-600 shadow-lg shadow-indigo-200">
                        <Megaphone className="h-6 w-6 text-white" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-gray-900">Criar Campanhas em Lote</h1>
                        <p className="mt-0.5 text-sm text-gray-500">Importe campanhas via JSON ou CSV, edite e crie em massa</p>
                    </div>
                </div>
            </div>

            {step === 'upload' && (
                <div className="space-y-6">
                    {/* Domain Selection */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                        <div className="flex items-center gap-2 mb-4">
                            <Globe className="h-5 w-5 text-gray-400" />
                            <h2 className="text-sm font-semibold text-gray-900">1. Selecione o Dominio</h2>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {configuredDomains.map((d) => {
                                const isSelected = selectedDomainId === d.id;
                                return (
                                    <button
                                        key={d.id}
                                        onClick={() => setSelectedDomainId(d.id)}
                                        className={`relative flex items-center gap-3 rounded-xl border-2 p-4 transition-all text-left ${
                                            isSelected ? 'border-indigo-500 bg-indigo-50/30 shadow-sm' : 'border-gray-100 hover:border-gray-300'
                                        }`}
                                    >
                                        {isSelected && <div className="absolute top-2.5 right-2.5 h-3 w-3 rounded-full bg-indigo-500" />}
                                        <img
                                            src={`https://www.google.com/s2/favicons?domain=${d.domain}&sz=64`}
                                            alt="" className="h-9 w-9 rounded-lg"
                                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                        />
                                        <div className="min-w-0">
                                            <p className={`text-sm font-semibold truncate ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>{d.domain}</p>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Download Template */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                        <div className="flex items-center gap-2 mb-4">
                            <Download className="h-5 w-5 text-gray-400" />
                            <h2 className="text-sm font-semibold text-gray-900">2. Baixe o Modelo</h2>
                        </div>
                        <p className="text-xs text-gray-500 mb-4">Baixe um template com os campos necessarios, preencha com suas campanhas e depois faca o upload.</p>
                        <div className="flex gap-3">
                            <Button variant="outline" className="gap-2 rounded-xl" onClick={downloadJsonTemplate}>
                                <FileJson className="h-4 w-4 text-amber-600" /> Template JSON
                            </Button>
                            <Button variant="outline" className="gap-2 rounded-xl" onClick={downloadCsvTemplate}>
                                <FileSpreadsheet className="h-4 w-4 text-emerald-600" /> Template CSV
                            </Button>
                        </div>
                        <div className="mt-4 rounded-xl border border-gray-100 bg-gray-50 p-4">
                            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2">Campos disponiveis</p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                {TEMPLATE_FIELDS.map(f => (
                                    <div key={f.key} className="flex items-center gap-1.5 text-xs">
                                        {f.required ? (
                                            <span className="h-1.5 w-1.5 rounded-full bg-red-400 shrink-0" />
                                        ) : (
                                            <span className="h-1.5 w-1.5 rounded-full bg-gray-300 shrink-0" />
                                        )}
                                        <span className="font-mono text-gray-600">{f.key}</span>
                                        {f.required && <span className="text-red-400 text-[10px]">*</span>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Upload / Editor */}
                    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
                        <div className="flex items-center gap-2 p-6 pb-0">
                            <Upload className="h-5 w-5 text-gray-400" />
                            <h2 className="text-sm font-semibold text-gray-900">3. Carregar Campanhas</h2>
                        </div>

                        {/* Tab toggle */}
                        <div className="flex gap-1 mx-6 mt-4 rounded-lg bg-gray-100/80 p-1">
                            <button
                                onClick={() => setUploadMode('file')}
                                className={`flex-1 flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-medium transition-all ${
                                    uploadMode === 'file' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                <Upload className="h-3.5 w-3.5" /> Upload Arquivo
                            </button>
                            <button
                                onClick={() => setUploadMode('editor')}
                                className={`flex-1 flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-medium transition-all ${
                                    uploadMode === 'editor' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                <Code className="h-3.5 w-3.5" /> Editor JSON
                            </button>
                        </div>

                        <div className="p-6">
                            {uploadMode === 'file' && (
                                <>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".json,.csv"
                                        className="hidden"
                                        onChange={handleFileUpload}
                                    />
                                    <button
                                        onClick={() => selectedDomainId ? fileInputRef.current?.click() : toast({ variant: 'destructive', title: 'Selecione um dominio primeiro' })}
                                        className={`w-full flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-10 transition-all ${
                                            selectedDomainId
                                                ? 'border-indigo-300 bg-indigo-50/30 hover:border-indigo-400 hover:bg-indigo-50/50 cursor-pointer'
                                                : 'border-gray-200 bg-gray-50/30 cursor-not-allowed opacity-60'
                                        }`}
                                    >
                                        <Upload className={`h-8 w-8 ${selectedDomainId ? 'text-indigo-400' : 'text-gray-300'}`} />
                                        <p className="mt-3 text-sm font-medium text-gray-700">
                                            Arraste ou clique para selecionar
                                        </p>
                                        <p className="mt-1 text-xs text-gray-400">Aceita .json ou .csv</p>
                                    </button>
                                </>
                            )}

                            {uploadMode === 'editor' && (
                                <div className="space-y-3">
                                    <textarea
                                        value={jsonCode}
                                        onChange={(e) => { setJsonCode(e.target.value); setJsonError(null); }}
                                        spellCheck={false}
                                        className="w-full h-80 rounded-xl border border-gray-200 bg-gray-950 text-gray-100 p-4 font-mono text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y"
                                        placeholder='[\n  { "name": "...", "title": "...", "body": "...", "url": "...", "type": "scheduled", "scheduled_at": "2026-03-20T10:00" }\n]'
                                    />
                                    {jsonError && (
                                        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                                            <AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                                            <p className="text-xs text-red-600 font-mono">{jsonError}</p>
                                        </div>
                                    )}
                                    <div className="flex items-center justify-between">
                                        <p className="text-[10px] text-gray-400">Cole ou edite o JSON das campanhas. Aceita array de objetos ou objeto unico.</p>
                                        <Button
                                            size="sm"
                                            className="gap-2 bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-sm"
                                            disabled={!selectedDomainId}
                                            onClick={handleJsonSubmit}
                                        >
                                            <Check className="h-3.5 w-3.5" /> Carregar Campanhas
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {step === 'preview' && (
                <div className="space-y-6">
                    {/* Summary bar */}
                    <div className="flex items-center gap-4">
                        <Button variant="outline" size="sm" className="gap-1.5 rounded-xl" onClick={() => { setStep('upload'); setItems([]); setEditingIdx(null); setEditDraft(null); }}>
                            <ArrowLeft className="h-4 w-4" /> Voltar ao Upload
                        </Button>
                        <div className="flex-1" />
                        <div className="flex items-center gap-3 text-sm">
                            <span className="flex items-center gap-1.5 text-emerald-600 font-medium">
                                <Check className="h-4 w-4" /> {validItems.length} validas
                            </span>
                            {invalidItems.length > 0 && (
                                <span className="flex items-center gap-1.5 text-red-500 font-medium">
                                    <AlertTriangle className="h-4 w-4" /> {invalidItems.length} com erros
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Invalid Items Warning */}
                    {invalidItems.length > 0 && (
                        <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <AlertTriangle className="h-4 w-4 text-red-500" />
                                <p className="text-sm font-semibold text-red-700">{invalidItems.length} campanhas com erros</p>
                            </div>
                            <p className="text-xs text-red-600">Edite as campanhas para corrigir os erros, ou remova-as da lista.</p>
                        </div>
                    )}

                    {/* Campaign Cards */}
                    <div className="space-y-3">
                        {items.map((item, idx) => {
                            const isEditing = editingIdx === idx;
                            const typeLabel = item.type === 'scheduled' ? 'Agendada' : item.type === 'recurring' ? 'Recorrente' : 'Direta';

                            return (
                                <div
                                    key={idx}
                                    className={`rounded-2xl border bg-white shadow-sm transition-all ${
                                        isEditing ? 'border-indigo-300 shadow-md' : item._valid ? 'border-gray-100' : 'border-red-200 bg-red-50/30'
                                    }`}
                                >
                                    {/* Card header */}
                                    <div className="flex items-center gap-4 p-4">
                                        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                                            isEditing ? 'bg-indigo-100' : item._valid ? 'bg-indigo-50' : 'bg-red-100'
                                        }`}>
                                            {isEditing ? (
                                                <Pencil className="h-5 w-5 text-indigo-600" />
                                            ) : item._valid ? (
                                                <Megaphone className="h-5 w-5 text-indigo-600" />
                                            ) : (
                                                <AlertTriangle className="h-5 w-5 text-red-500" />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <p className="text-sm font-semibold text-gray-900 truncate">{item.name || 'Sem nome'}</p>
                                                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                                                    item.type === 'scheduled' ? 'bg-blue-50 text-blue-600' : item.type === 'recurring' ? 'bg-purple-50 text-purple-600' : 'bg-gray-100 text-gray-600'
                                                }`}>
                                                    {item.type === 'scheduled' && <Clock className="h-2.5 w-2.5" />}
                                                    {item.type === 'recurring' && <Repeat className="h-2.5 w-2.5" />}
                                                    {typeLabel}
                                                </span>
                                                {item._valid && !isEditing && (
                                                    <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                                                )}
                                            </div>
                                            {!isEditing && (
                                                <div className="flex items-start gap-2.5 mt-2">
                                                    {item.icon ? (
                                                        <img src={item.icon} alt="" className="h-8 w-8 rounded-md object-cover shrink-0" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                                    ) : (
                                                        <div className="h-8 w-8 rounded-md bg-gray-100 flex items-center justify-center shrink-0">
                                                            <Bell className="h-3.5 w-3.5 text-gray-400" />
                                                        </div>
                                                    )}
                                                    <div className="min-w-0">
                                                        <p className="text-xs font-semibold text-gray-800 truncate">{item.title || 'Sem titulo'}</p>
                                                        <p className="text-[11px] text-gray-500 line-clamp-1">{item.body || 'Sem mensagem'}</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-1 shrink-0">
                                            {!isEditing && (
                                                <>
                                                    <button onClick={() => setPreviewItem(item)} className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors" title="Preview">
                                                        <Eye className="h-3.5 w-3.5" />
                                                    </button>
                                                    <button onClick={() => startEditing(idx)} className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors" title="Editar">
                                                        <Pencil className="h-3.5 w-3.5" />
                                                    </button>
                                                    <button onClick={() => removeItem(idx)} className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors" title="Remover">
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Error badges */}
                                    {!isEditing && !item._valid && item._errors && (
                                        <div className="px-4 pb-3">
                                            <div className="flex flex-wrap gap-1.5">
                                                {item._errors.map((err, ei) => (
                                                    <span key={ei} className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-[11px] font-medium text-red-700">
                                                        <X className="h-3 w-3" /> {err}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Inline Edit Form */}
                                    {isEditing && editDraft && (
                                        <div className="border-t border-indigo-100 px-5 py-5 space-y-5">
                                            {/* Content */}
                                            <div className="space-y-3">
                                                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Conteudo</p>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div className="col-span-2 sm:col-span-1">
                                                        <Label className="text-xs font-medium text-gray-600">Nome da Campanha *</Label>
                                                        <Input
                                                            value={editDraft.name || ''}
                                                            onChange={(e) => updateDraft('name', e.target.value)}
                                                            className="mt-1 rounded-lg text-sm"
                                                            placeholder="Ex: Promo Black Friday"
                                                        />
                                                    </div>
                                                    <div className="col-span-2 sm:col-span-1">
                                                        <Label className="text-xs font-medium text-gray-600">URL de destino *</Label>
                                                        <Input
                                                            value={editDraft.url || ''}
                                                            onChange={(e) => updateDraft('url', e.target.value)}
                                                            className="mt-1 rounded-lg text-sm"
                                                            placeholder="https://..."
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <Label className="text-xs font-medium text-gray-600">Titulo da Push *</Label>
                                                    <Input
                                                        value={editDraft.title || ''}
                                                        onChange={(e) => updateDraft('title', e.target.value)}
                                                        className="mt-1 rounded-lg text-sm"
                                                        placeholder="Titulo que aparece na notificacao"
                                                    />
                                                </div>
                                                <div>
                                                    <Label className="text-xs font-medium text-gray-600">Mensagem *</Label>
                                                    <Textarea
                                                        value={editDraft.body || ''}
                                                        onChange={(e) => updateDraft('body', e.target.value)}
                                                        rows={2}
                                                        className="mt-1 rounded-lg text-sm"
                                                        placeholder="Texto da notificacao"
                                                    />
                                                </div>
                                            </div>

                                            {/* Type */}
                                            <div className="space-y-3">
                                                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Tipo de Envio</p>
                                                <div className="flex gap-2">
                                                    {[
                                                        { val: 'direct', label: 'Direta', icon: Send, desc: 'Enviar agora' },
                                                        { val: 'scheduled', label: 'Agendada', icon: Clock, desc: 'Data/hora' },
                                                        { val: 'recurring', label: 'Recorrente', icon: Repeat, desc: 'Cron' },
                                                    ].map((opt) => {
                                                        const Icon = opt.icon;
                                                        const isSelected = (editDraft.type || 'direct') === opt.val;
                                                        return (
                                                            <button
                                                                key={opt.val}
                                                                onClick={() => updateDraft('type', opt.val)}
                                                                className={`flex items-center gap-2 rounded-xl border-2 px-3 py-2.5 text-xs font-semibold transition-all flex-1 justify-center ${
                                                                    isSelected ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-100 text-gray-500 hover:border-gray-300'
                                                                }`}
                                                            >
                                                                <Icon className={`h-3.5 w-3.5 ${isSelected ? 'text-indigo-500' : 'text-gray-400'}`} />
                                                                {opt.label}
                                                            </button>
                                                        );
                                                    })}
                                                </div>

                                                {editDraft.type === 'scheduled' && (
                                                    <div>
                                                        <Label className="text-xs font-medium text-gray-600">Agendar para *</Label>
                                                        <Input
                                                            type="datetime-local"
                                                            value={editDraft.scheduled_at?.replace('Z', '').slice(0, 16) || ''}
                                                            onChange={(e) => updateDraft('scheduled_at', e.target.value || '')}
                                                            className="mt-1 rounded-lg text-sm w-64"
                                                        />
                                                        <p className="text-[10px] text-gray-400 mt-1">Horario local (seu fuso)</p>
                                                    </div>
                                                )}

                                                {editDraft.type === 'recurring' && (
                                                    <div className="space-y-2">
                                                        <Label className="text-xs font-medium text-gray-600">Cron *</Label>
                                                        <div className="flex flex-wrap gap-1.5">
                                                            {CRON_PRESETS.map((p) => (
                                                                <button
                                                                    key={p.value}
                                                                    onClick={() => updateDraft('recurring_cron', p.value)}
                                                                    className={`rounded-lg px-2.5 py-1 text-[11px] font-medium transition-all ${
                                                                        editDraft.recurring_cron === p.value ? 'bg-indigo-600 text-white' : 'bg-gray-50 border border-gray-200 text-gray-600 hover:border-gray-300'
                                                                    }`}
                                                                >
                                                                    {p.label}
                                                                </button>
                                                            ))}
                                                        </div>
                                                        <Input
                                                            value={editDraft.recurring_cron || ''}
                                                            onChange={(e) => updateDraft('recurring_cron', e.target.value)}
                                                            className="rounded-lg text-sm font-mono"
                                                            placeholder="*/5 * * * *"
                                                        />
                                                    </div>
                                                )}
                                            </div>

                                            {/* Media */}
                                            <div className="space-y-3">
                                                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Midia</p>
                                                {/* Icon */}
                                                <div>
                                                    <Label className="text-xs font-medium text-gray-600">Icone</Label>
                                                    <div className="mt-1.5 flex items-center gap-3">
                                                        {editDraft.icon ? (
                                                            <div className="relative shrink-0">
                                                                <img src={editDraft.icon} alt="icon" className="h-12 w-12 rounded-lg object-cover border" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                                                <button onClick={() => updateDraft('icon', '')} className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-500 text-white flex items-center justify-center"><X className="h-3 w-3" /></button>
                                                            </div>
                                                        ) : null}
                                                        <button onClick={() => setShowMediaPicker('icon')} className="flex items-center gap-2 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors shrink-0">
                                                            <ImageIcon className="h-3.5 w-3.5" /> Biblioteca
                                                        </button>
                                                        <Input value={editDraft.icon || ''} onChange={(e) => updateDraft('icon', e.target.value)} placeholder="ou cole a URL do icone" className="flex-1 rounded-lg text-sm h-9" />
                                                    </div>
                                                </div>
                                                {/* Image */}
                                                <div>
                                                    <Label className="text-xs font-medium text-gray-600">Imagem</Label>
                                                    <div className="mt-1.5 space-y-2">
                                                        {editDraft.image && (
                                                            <div className="relative inline-block">
                                                                <img src={editDraft.image} alt="banner" className="h-24 rounded-lg object-cover border" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                                                <button onClick={() => updateDraft('image', '')} className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-500 text-white flex items-center justify-center"><X className="h-3 w-3" /></button>
                                                            </div>
                                                        )}
                                                        <div className="flex items-center gap-3">
                                                            <button onClick={() => setShowMediaPicker('image')} className="flex items-center gap-2 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors shrink-0">
                                                                <ImageIcon className="h-3.5 w-3.5" /> Biblioteca
                                                            </button>
                                                            <Input value={editDraft.image || ''} onChange={(e) => updateDraft('image', e.target.value)} placeholder="ou cole a URL da imagem" className="flex-1 rounded-lg text-sm h-9" />
                                                        </div>
                                                    </div>
                                                </div>
                                                {/* Badge */}
                                                <div>
                                                    <Label className="text-xs font-medium text-gray-600">Badge <span className="text-gray-300">(opcional)</span></Label>
                                                    <div className="mt-1.5 flex items-center gap-3">
                                                        {editDraft.badge ? (
                                                            <div className="relative shrink-0">
                                                                <img src={editDraft.badge} alt="badge" className="h-8 w-8 rounded object-cover border" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                                                <button onClick={() => updateDraft('badge', '')} className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-red-500 text-white flex items-center justify-center"><X className="h-2.5 w-2.5" /></button>
                                                            </div>
                                                        ) : null}
                                                        <button onClick={() => setShowMediaPicker('badge')} className="flex items-center gap-2 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors shrink-0">
                                                            <ImageIcon className="h-3.5 w-3.5" /> Biblioteca
                                                        </button>
                                                        <Input value={editDraft.badge || ''} onChange={(e) => updateDraft('badge', e.target.value)} placeholder="ou cole a URL do badge" className="flex-1 rounded-lg text-sm h-9" />
                                                    </div>
                                                </div>
                                                {/* Media Picker Modal */}
                                                {showMediaPicker && (
                                                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowMediaPicker(null)}>
                                                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[75vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                                                            <div className="flex items-center justify-between p-4 border-b">
                                                                <h3 className="text-sm font-semibold text-gray-900">
                                                                    {{ icon: 'Escolher Icone', image: 'Escolher Imagem', badge: 'Escolher Badge' }[showMediaPicker]}
                                                                </h3>
                                                                <button onClick={() => setShowMediaPicker(null)} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
                                                            </div>
                                                            <div className="px-4 pt-4">
                                                                <label className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-5 cursor-pointer transition-all ${uploading ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:border-gray-400 hover:bg-gray-50'}`}>
                                                                    <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => { const file = e.target.files?.[0]; if (file) handleMediaUpload(file); e.target.value = ''; }} />
                                                                    {uploading ? (
                                                                        <><Loader2 className="h-6 w-6 text-blue-500 animate-spin" /><p className="mt-2 text-xs text-blue-600 font-medium">Enviando...</p></>
                                                                    ) : (
                                                                        <><UploadIcon className="h-6 w-6 text-gray-400" /><p className="mt-2 text-xs text-gray-600 font-medium">Fazer upload</p><p className="text-[11px] text-gray-400">JPG, PNG, GIF, WebP, SVG</p></>
                                                                    )}
                                                                </label>
                                                            </div>
                                                            <div className="px-4 pt-3 pb-1"><p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Biblioteca</p></div>
                                                            <div className="flex-1 overflow-y-auto px-4 pb-4">
                                                                <div className="grid grid-cols-4 gap-2.5">
                                                                    {(showMediaPicker === 'icon' || showMediaPicker === 'badge' ? mediaList.filter(m => m.media_type === 'icon') : mediaList.filter(m => m.media_type === 'image')).map((media) => (
                                                                        <button key={media.id} onClick={() => { updateDraft(showMediaPicker!, getMediaUrl(media.filepath)); setShowMediaPicker(null); }} className="group relative rounded-lg border-2 border-transparent hover:border-gray-900 overflow-hidden transition-all">
                                                                            <img src={getThumbnailUrl(media)} alt={media.original_filename} className="w-full aspect-square object-cover" />
                                                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center"><Check className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" /></div>
                                                                        </button>
                                                                    ))}
                                                                    {mediaList.filter(m => (showMediaPicker === 'icon' || showMediaPicker === 'badge') ? m.media_type === 'icon' : m.media_type === 'image').length === 0 && (
                                                                        <div className="col-span-4 py-6 text-center text-sm text-gray-400">Nenhuma midia encontrada. Faca upload acima.</div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Tracking */}
                                            <div className="space-y-3">
                                                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Rastreamento</p>
                                                <div className="grid grid-cols-3 gap-3">
                                                    <div>
                                                        <Label className="text-xs font-medium text-gray-600">UTM Campaign</Label>
                                                        <Input
                                                            value={editDraft.utm_campaign || ''}
                                                            onChange={(e) => updateDraft('utm_campaign', e.target.value)}
                                                            className="mt-1 rounded-lg text-sm"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label className="text-xs font-medium text-gray-600">UTM Medium</Label>
                                                        <Input
                                                            value={editDraft.utm_medium || ''}
                                                            onChange={(e) => updateDraft('utm_medium', e.target.value)}
                                                            className="mt-1 rounded-lg text-sm"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label className="text-xs font-medium text-gray-600">UTM Source</Label>
                                                        <Input
                                                            value={editDraft.utm_source || ''}
                                                            onChange={(e) => updateDraft('utm_source', e.target.value)}
                                                            className="mt-1 rounded-lg text-sm"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <Label className="text-xs font-medium text-gray-600">Tag ao receber</Label>
                                                        <Input
                                                            value={editDraft.tag_on_receive || ''}
                                                            onChange={(e) => updateDraft('tag_on_receive', e.target.value)}
                                                            className="mt-1 rounded-lg text-sm"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label className="text-xs font-medium text-gray-600">Tag ao clicar</Label>
                                                        <Input
                                                            value={editDraft.tag_on_click || ''}
                                                            onChange={(e) => updateDraft('tag_on_click', e.target.value)}
                                                            className="mt-1 rounded-lg text-sm"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Push Preview */}
                                            <div className="rounded-xl border border-indigo-100 bg-indigo-50/50 p-3">
                                                <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-400 mb-2">Preview</p>
                                                <div className="rounded-lg bg-white shadow-sm border border-gray-100 overflow-hidden">
                                                    {editDraft.image && (
                                                        <img src={editDraft.image} alt="" className="w-full h-28 object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                                    )}
                                                    <div className="p-3">
                                                        <div className="flex items-start gap-2.5">
                                                            {editDraft.icon ? (
                                                                <img src={editDraft.icon} alt="" className="h-10 w-10 rounded-lg object-cover shrink-0" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                                            ) : (
                                                                <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                                                                    <Bell className="h-4 w-4 text-gray-400" />
                                                                </div>
                                                            )}
                                                            <div className="min-w-0 flex-1">
                                                                <div className="flex items-center gap-1.5 mb-0.5">
                                                                    <span className="text-[9px] text-gray-400 font-medium">{selectedDomain || 'seusite.com'}</span>
                                                                    <span className="text-[9px] text-gray-300 ml-auto">agora</span>
                                                                </div>
                                                                <p className="text-xs font-bold text-gray-900 truncate">{editDraft.title || 'Titulo'}</p>
                                                                <p className="text-[11px] text-gray-500 line-clamp-2 mt-0.5">{editDraft.body || 'Mensagem...'}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Save / Cancel */}
                                            <div className="flex items-center gap-3 pt-1">
                                                <Button variant="outline" size="sm" className="rounded-xl" onClick={cancelEditing}>
                                                    Cancelar
                                                </Button>
                                                <Button size="sm" className="rounded-xl bg-indigo-600 hover:bg-indigo-700 gap-2 shadow-sm" onClick={saveEditing}>
                                                    <Save className="h-4 w-4" />
                                                    Salvar Alteracoes
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Submit */}
                    {items.length > 0 && (
                        <div className="flex items-center justify-between rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                            <div>
                                <p className="text-sm font-semibold text-gray-900">
                                    {validItems.length} campanha{validItems.length !== 1 ? 's' : ''} pronta{validItems.length !== 1 ? 's' : ''} para criar
                                </p>
                                {invalidItems.length > 0 && (
                                    <p className="text-xs text-gray-500 mt-0.5">{invalidItems.length} serao ignoradas por conter erros</p>
                                )}
                            </div>
                            <Button
                                className="gap-2 bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-sm"
                                disabled={validItems.length === 0 || batchMutation.isPending || editingIdx !== null}
                                onClick={handleSubmit}
                            >
                                {batchMutation.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Send className="h-4 w-4" />
                                )}
                                Criar {validItems.length} Campanha{validItems.length !== 1 ? 's' : ''}
                            </Button>
                        </div>
                    )}
                </div>
            )}

            {/* Preview Modal */}
            <Dialog open={!!previewItem} onOpenChange={(o) => !o && setPreviewItem(null)}>
                <DialogContent className="max-w-sm rounded-2xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-sm">
                            <Eye className="h-4 w-4 text-indigo-500" /> Preview da Notificacao
                        </DialogTitle>
                    </DialogHeader>
                    {previewItem && (
                        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                            {previewItem.image && (
                                <img src={previewItem.image} alt="" className="w-full h-36 object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                            )}
                            <div className="p-4">
                                <div className="flex items-start gap-3">
                                    {previewItem.icon ? (
                                        <img src={previewItem.icon} alt="" className="h-10 w-10 rounded-lg object-cover shrink-0" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                    ) : (
                                        <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                                            <Megaphone className="h-4 w-4 text-gray-400" />
                                        </div>
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-1.5 mb-1">
                                            <span className="text-[9px] text-gray-400 font-medium">
                                                {selectedDomain || 'seusite.com'}
                                            </span>
                                            <span className="text-[9px] text-gray-300 ml-auto">agora</span>
                                        </div>
                                        <p className="text-sm font-bold text-gray-900">{previewItem.title}</p>
                                        <p className="text-xs text-gray-500 mt-0.5">{previewItem.body}</p>
                                    </div>
                                </div>
                                {previewItem.url && (
                                    <p className="mt-3 text-[11px] text-blue-500 truncate">{previewItem.url}</p>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
